import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../Auth/context/AuthContext.js';
import { Trash2 } from 'lucide-react';

export default function SeatsPage() {
  const { auth } = useContext(AuthContext);
  const isAdmin = auth.user_type === 'Organization' || auth.user_type === 'admin';
  const [seats, setSeats] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [newSeatRole, setNewSeatRole] = useState('Project Manager');
  const [subscription, setSubscription] = useState({ seatsUsed: 0, seatsAllowed: 0 });
  const [statusMessage, setStatusMessage] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [projects, setProjects] = useState([]);

  const fetchSeats = useCallback(() => {
    axios.get('/api/myteconstruction/seats', {
      headers: { Authorization: `Bearer ${auth.token}` },
      params: {
        ...(roleFilter ? { role: roleFilter } : {}),
        status: showInactive ? 'inactive' : 'active',
        ...(projectFilter !== '' ? { projectId: projectFilter } : {}),
      },
    })
    .then(res => setSeats(res.data || []))
    .catch(err => { console.error('Error fetching seats:', err); setStatusMessage('Failed to load seats.'); });
  }, [auth.token, roleFilter, showInactive, projectFilter]);

  const fetchSubscription = useCallback(() => {
    axios.get('/api/myteconstruction/organization/me', { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(res => setSubscription(res.data.organization?.subscription || { seatsUsed: 0, seatsAllowed: 0 }))
      .catch(err => console.error('Error fetching subscription:', err));
  }, [auth.token]);

  useEffect(() => {
    if (!auth.token) return;
    fetchSeats();
    fetchSubscription();
    axios.get('/api/myteconstruction/projects', { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(res => setProjects(res.data.data || []))
      .catch(err => console.error('Error fetching projects:', err));
  }, [auth.token, fetchSeats, fetchSubscription]);

  const handleRemove = seatId => {
    if (!window.confirm('Remove user from this seat?')) return;
    axios.post(`/api/myteconstruction/seats/${seatId}/remove`, {}, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(() => { setStatusMessage('Seat freed.'); fetchSubscription(); fetchSeats(); })
      .catch(err => { console.error('Error removing seat:', err); setStatusMessage('Failed to remove seat.'); });
  };

  const handleAddSeat = () => {
    axios.post('/api/myteconstruction/seats', { role: newSeatRole }, { headers: { Authorization: `Bearer ${auth.token}` } })
      .then(() => { setStatusMessage('New seat added.'); fetchSeats(); })
      .catch(err => { console.error('Error adding seat:', err); setStatusMessage(err.response?.data?.error || 'Failed to add seat.'); });
  };

  if (!auth.token) return <div className="p-4 bg-gray-950 text-white">Please log in to view Seats.</div>;

  return (
    <div className="p-4 bg-gray-950 text-white min-h-screen">
      <h2 className="text-2xl font-bold mb-4">Seats Management</h2>
      <p>Seats used: {subscription.seatsUsed} / {subscription.seatsAllowed}</p>
      {isAdmin && (
        <div className="flex items-center space-x-2 my-4">
          <select value={newSeatRole} onChange={e => setNewSeatRole(e.target.value)} className="bg-gray-900 p-2 rounded">
            <option>Project Manager</option>
            <option>Foreman</option>
          </select>
          <button onClick={handleAddSeat} className="bg-blue-600 px-3 py-1 rounded hover:bg-blue-700">Add Seat</button>
        </div>
      )}
      <div className="flex items-center space-x-4 mb-4">
        <select value={projectFilter} onChange={e=>setProjectFilter(e.target.value)} className="bg-gray-900 p-2 rounded">
          <option value="">All Projects</option>
          <option value="null">Unassigned</option>
          {projects.map(p=> <option key={p._id} value={p._id}>{p.projectCode||p.title}</option>)}
        </select>
        <select value={roleFilter} onChange={e=>setRoleFilter(e.target.value)} className="bg-gray-900 p-2 rounded">
          <option value="">All Roles</option>
          <option>Project Manager</option>
          <option>Foreman</option>
        </select>
        <button onClick={()=>setShowInactive(false)} disabled={!showInactive} className="px-2 py-1 bg-blue-600 rounded disabled:opacity-50">Active</button>
        <button onClick={()=>setShowInactive(true)} disabled={showInactive} className="px-2 py-1 bg-blue-600 rounded disabled:opacity-50">Inactive</button>
      </div>
      {statusMessage && <div className="mb-4 text-red-500">{statusMessage}</div>}
      <div className="grid grid-cols-2 gap-4">
        {['Idle','In Project'].map(col => (
          <div key={col}>
            <h3 className="font-semibold mb-2">{col}</h3>
            <div className="space-y-2">
              {seats.filter(seat => (
                col==='Idle' ? seat.status==='active' && !seat.projectId : seat.status==='active' && !!seat.projectId
              )).map(seat => (
                <div key={seat._id} className="p-3 bg-gray-900 rounded">
                  <p className="font-medium">{seat.role}</p>
                  <p className="mt-1 text-sm">{seat.projectId ? seat.userId.name : <em>Unassigned</em>}</p>
                  {col==='In Project' && <p className="text-xs text-gray-400">{seat.projectId.projectCode || seat.projectId.title}</p>}
                  {col==='In Project' && isAdmin && (
                    <button onClick={() => handleRemove(seat._id)} className="mt-2 bg-red-600 px-2 py-1 rounded hover:bg-red-700 text-white flex items-center gap-1"><Trash2 className="h-4 w-4" /> Remove</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}